import { ReactNode, ReactElement, useState, useCallback, useMemo } from 'react';
import {
  styled,
  Box,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemText,
  Collapse,
  ListItemIcon,
  Checkbox,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import classNames from 'classnames';

import { useToggle, useSubscriptionFeatures } from '~hooks/index';
import AccessFeature from '~components/AccessFeature';
import { AccessFeatureCommonProps } from '~components/AccessFeature/types';

export type Layers =
  | 'markers'
  | 'trips'
  | 'vehicles'
  | 'routeItemState'
  | 'orders'
  | 'warehouses'
  | 'zones'
  | 'sequenceStops'
  | 'routesOnAgenda'
  | 'routesInProgress';

type LayersSelectors = Record<Layers, boolean>;

interface LayersListItem {
  name: Layers;
  label: string;
  enabled: boolean;
}

type LayersSelectorProps = {
  checkedLayers: LayersSelectors;
  layersList: LayersListItem[];
  toggleLayer: (name: Layers) => void;
  validSubscriptionFeature?: LayerControlValidSubscriptionFeature;
};

type LayersControlHidden = {
  [Property in keyof LayersSelectors as `hidden${Capitalize<
    string & Property
  >}`]?: LayersSelectors[Property];
};

type LayerControlEnabled = {
  [Property in keyof LayersSelectors as `enabled${Capitalize<
    string & Property
  >}`]?: LayersSelectors[Property];
} & LayersSelectorProps;

type LayerControlValidSubscriptionFeature = {
  [state in Layers]?: AccessFeatureCommonProps;
};

interface LayersControlProps extends LayersControlHidden {
  onToggle?: (layerKey: Layers, checked: boolean) => void;
  validSubscriptionFeature?: LayerControlValidSubscriptionFeature;
  children: (layers: LayerControlEnabled) => ReactNode;
}

export const classes = {
  expanded: 'LayersControl-expanded',
  expandIcon: 'LayersControl-expandIcon',
};

export const LayersControlContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: 250,
  background: theme.palette.common.white,
  outline: 'none',
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(1.25),
  boxShadow: '0 0 0 2px rgb(0 0 0 / 10%)',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
}));

export const LayersControlListItemButton = styled(ListItemButton)(
  ({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(
      ['border-radius', 'background-color'],
      {
        duration: theme.transitions.duration.shortest,
      },
    ),

    [`& .${classes.expandIcon}`]: {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.action.active,
      transform: 'rotate(0deg)',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
      }),
    },

    [`&.${classes.expanded}`]: {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,

      [`& .${classes.expandIcon}`]: {
        transform: 'rotate(180deg)',
      },
    },
  }),
);

export const LayersControlListItemButtonAccessFeature = styled(
  AccessFeature.ListItemButton,
)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
}));

export const LayersControlListItemText = styled(ListItemText)({
  margin: 0,
});

const LayersControlListIcon = styled(ListItemIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: 'auto',
}));

const LayersControlListItemCheckbox = styled(Checkbox)({
  padding: 0,
});

const LayersControlListItemButtonAction = ({
  validSubscriptionFeature,
  ...restProps
}: ListItemButtonProps & {
  validSubscriptionFeature?: AccessFeatureCommonProps;
}) => {
  if (validSubscriptionFeature) {
    return (
      <LayersControlListItemButtonAccessFeature
        {...validSubscriptionFeature}
        {...restProps}
      />
    );
  }

  return <LayersControlListItemButton {...restProps} />;
};

export const LayersSelector = ({
  checkedLayers,
  layersList,
  toggleLayer,
  validSubscriptionFeature,
}: LayersSelectorProps): ReactElement => {
  const [expanded, toogleExpanded] = useToggle(true);

  return (
    <LayersControlContainer>
      <List disablePadding>
        <LayersControlListItemButton
          onClick={() => toogleExpanded()}
          className={classNames({ [classes.expanded]: expanded })}
        >
          <LayersControlListItemText
            primary="Capas"
            primaryTypographyProps={{
              variant: 'caption',
              fontWeight: 'bold',
            }}
          />

          <KeyboardArrowDown className={classes.expandIcon} />
        </LayersControlListItemButton>

        <Collapse in={expanded} timeout="auto">
          <List disablePadding>
            {layersList.map(
              ({ name, label, enabled }) =>
                enabled && (
                  <LayersControlListItemButtonAction
                    key={`layers-control-item-${name}`}
                    onClick={() => toggleLayer(name)}
                    validSubscriptionFeature={validSubscriptionFeature?.[name]}
                  >
                    <LayersControlListIcon>
                      <LayersControlListItemCheckbox
                        checked={checkedLayers[name]}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </LayersControlListIcon>

                    <LayersControlListItemText
                      primary={label}
                      primaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </LayersControlListItemButtonAction>
                ),
            )}
          </List>
        </Collapse>
      </List>
    </LayersControlContainer>
  );
};

const LayersControl = ({
  hiddenMarkers = false,
  hiddenTrips = false,
  hiddenVehicles = false,
  hiddenRouteItemState = false,
  hiddenOrders = true,
  hiddenWarehouses = true,
  hiddenZones = true,
  hiddenSequenceStops = true,
  hiddenRoutesOnAgenda = true,
  hiddenRoutesInProgress = true,
  validSubscriptionFeature,
  onToggle,
  children,
}: LayersControlProps): ReactElement => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const layersList = useMemo<LayersListItem[]>(
    () => [
      {
        name: 'markers',
        label: 'Marcadores',
        enabled: !hiddenMarkers,
      },
      {
        name: 'sequenceStops',
        label: 'Secuencia de paradas',
        enabled: !hiddenSequenceStops,
      },
      {
        name: 'trips',
        label: 'Rutas',
        enabled: !hiddenTrips,
      },
      {
        name: 'vehicles',
        label: 'Vehículos',
        enabled: !hiddenVehicles,
      },
      {
        name: 'routeItemState',
        label: 'Estados de visita',
        enabled: !hiddenRouteItemState,
      },
      {
        name: 'orders',
        label: 'Pedidos',
        enabled: !hiddenOrders,
      },
      {
        name: 'warehouses',
        label: 'Depósitos',
        enabled: !hiddenWarehouses,
      },
      {
        name: 'zones',
        label: 'Zonas logísticas',
        enabled: !hiddenZones,
      },
      {
        name: 'routesOnAgenda',
        label: 'Rutas en agenda',
        enabled: !hiddenRoutesOnAgenda,
      },
      {
        name: 'routesInProgress',
        label: 'Rutas En tránsito',
        enabled: !hiddenRoutesInProgress,
      },
    ],
    [
      hiddenMarkers,
      hiddenTrips,
      hiddenVehicles,
      hiddenRouteItemState,
      hiddenOrders,
      hiddenWarehouses,
      hiddenZones,
      hiddenSequenceStops,
      hiddenRoutesOnAgenda,
      hiddenRoutesInProgress,
    ],
  );

  const getDefaultLayerCheckedValue = useCallback(
    (layerKey: Layers, defaultValue: boolean) => {
      const currentValidSubscriptionFeature =
        validSubscriptionFeature?.[layerKey];

      if (!currentValidSubscriptionFeature) return defaultValue;

      return hasAccessToFeature(
        currentValidSubscriptionFeature.validFeature,
        currentValidSubscriptionFeature.availableToDate,
      );
    },
    [hasAccessToFeature, validSubscriptionFeature],
  );

  const [checkedLayers, setCheckedLayers] = useState<LayersSelectors>({
    markers: true,
    trips: true,
    vehicles: true,
    routeItemState: true,
    orders: true,
    warehouses: true,
    zones: getDefaultLayerCheckedValue('zones', true),
    sequenceStops: false,
    routesOnAgenda: false,
    routesInProgress: false,
  });

  const toggleLayer = useCallback(
    (name: Layers) => {
      const value = !checkedLayers[name];

      setCheckedLayers((prevLayers) => ({
        ...prevLayers,
        [name]: !checkedLayers[name],
      }));

      onToggle?.(name, value);
    },
    [checkedLayers, onToggle],
  );

  return (
    <>
      {children({
        enabledMarkers: checkedLayers.markers,
        enabledTrips: checkedLayers.trips,
        enabledVehicles: checkedLayers.vehicles,
        enabledRouteItemState: checkedLayers.routeItemState,
        enabledOrders: checkedLayers.orders,
        enabledWarehouses: checkedLayers.warehouses,
        enabledZones: checkedLayers.zones,
        enabledSequenceStops: checkedLayers.sequenceStops,
        enabledRoutesOnAgenda: checkedLayers.routesOnAgenda,
        enabledRoutesInProgress: checkedLayers.routesInProgress,
        checkedLayers: checkedLayers,
        toggleLayer: toggleLayer,
        layersList: layersList,
      })}
    </>
  );
};

export default LayersControl;
